import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Button,
    Paper,
    CircularProgress,
    Alert,
    IconButton,
    Chip,
    Pagination,
    Skeleton
} from '@mui/material';
import { ArrowBack, Person, PersonAdd, PersonRemove } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { socialAPI, toggleFollow } from '../utils/api';

const FollowingList = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [following, setFollowing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [followingActions, setFollowingActions] = useState({});
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [userInfo, setUserInfo] = useState(null);

    const fetchFollowing = async (pageNum = 1) => {
        try {
            setLoading(true);
            const response = await socialAPI.getFollowing(userId, pageNum);
            setFollowing(response.data.following || []);
            setTotalPages(response.data.pagination?.pages || 1);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserInfo = async () => {
        try {
            const response = await socialAPI.getUserProfile(userId);
            setUserInfo(response.data?.user || null);
        } catch (err) {
            console.error('Error fetching user info:', err);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchFollowing(page);
            fetchUserInfo();
        }
    }, [userId, page]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFollow = async (targetUserId) => {
        if (followingActions[targetUserId]) return;

        try {
            setFollowingActions(prev => ({ ...prev, [targetUserId]: true }));
            
            const result = await toggleFollow(targetUserId);
            
            setFollowing(prev => prev.map(followedUser => 
                followedUser.id === targetUserId 
                    ? { ...followedUser, is_following: result.following }
                    : followedUser
            ));
        } catch (err) {
            console.error('Error toggling follow:', err);
        } finally {
            setFollowingActions(prev => ({ ...prev, [targetUserId]: false }));
        }
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const getFollowButtonProps = (followedUser) => {
        if (followedUser.is_current_user) {
            return null;
        }

        if (followedUser.is_following) {
            return {
                variant: 'outlined',
                startIcon: <PersonRemove />,
                children: 'Unfollow'
            };
        }

        return {
            variant: 'contained',
            startIcon: <PersonAdd />,
            children: followedUser.is_mutual ? 'Follow Back' : 'Follow'
        };
    };

    if (loading && page === 1) {
        return (
            <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                    {[1, 2, 3, 4].map((idx) => (
                        <Box key={idx} display="flex" alignItems="center" gap={1.5} sx={{ py: 1.2 }}>
                            <Skeleton variant="circular" width={40} height={40} />
                            <Box flex={1}>
                                <Skeleton variant="text" width="42%" />
                                <Skeleton variant="text" width="28%" />
                            </Box>
                            <Skeleton variant="rounded" width={96} height={34} />
                        </Box>
                    ))}
                </Paper>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
                <Alert severity="error">
                    {error}
                </Alert>
            </Container>
        );
    }

    const isCurrentUser = String(userId) === String(user?.id);

    return (
        <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
            <Paper
                variant="outlined"
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    mb: 3,
                    borderRadius: 3,
                    background:
                        'radial-gradient(220px 140px at 90% -30%, rgba(255, 110, 77, 0.18), transparent 70%), linear-gradient(135deg, rgba(14, 123, 255, 0.12), rgba(0, 165, 255, 0.07))',
                }}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton aria-label="Back" onClick={() => navigate(-1)}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 800 }}>
                        {userInfo ? `${userInfo.username}'s Following` : 'Following'}
                    </Typography>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
                {following.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Person sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            Not following anyone yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isCurrentUser ? 
                                "You're not following anyone yet. Discover some users to follow!" : 
                                "This user isn't following anyone yet."
                            }
                        </Typography>
                        {isCurrentUser && (
                            <Button 
                                variant="contained" 
                                onClick={() => navigate('/social')}
                                sx={{ mt: 2 }}
                            >
                                Discover Users
                            </Button>
                        )}
                    </Box>
                ) : (
                    <>
                        <List>
                            {following.map((followedUser) => {
                                const buttonProps = getFollowButtonProps(followedUser);
                                
                                return (
                                    <ListItem
                                        key={followedUser.id}
                                        divider
                                        sx={{
                                            borderRadius: 2,
                                            transition: 'background-color 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: 'rgba(14, 123, 255, 0.06)',
                                            },
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar 
                                                src={followedUser.avatar_url} 
                                                alt={followedUser.username}
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => navigate(`/profile/${followedUser.id}`)}
                                            />
                                        </ListItemAvatar>
                                        <ListItemText 
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography 
                                                        variant="subtitle1" 
                                                        component="span"
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => navigate(`/profile/${followedUser.id}`)}
                                                    >
                                                        {followedUser.username}
                                                    </Typography>
                                                    {followedUser.is_mutual && (
                                                        <Chip 
                                                            label="Mutual" 
                                                            size="small" 
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={followedUser.name}
                                        />
                                        {buttonProps && (
                                            <Button
                                                {...buttonProps}
                                                size="small"
                                                disabled={followingActions[followedUser.id]}
                                                onClick={() => handleFollow(followedUser.id)}
                                            >
                                                {followingActions[followedUser.id] ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    buttonProps.children
                                                )}
                                            </Button>
                                        )}
                                    </ListItem>
                                );
                            })}
                        </List>

                        {totalPages > 1 && (
                            <Box display="flex" justifyContent="center" mt={3}>
                                <Pagination 
                                    count={totalPages} 
                                    page={page} 
                                    onChange={handlePageChange}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </>
                )}
            </Paper>
        </Container>
    );
};

export default FollowingList;
